/**
 * Product Context Screen (Step 1)
 * Product summary and form: product_name, product_state, product_type.
 */
import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Card, Space, Typography } from 'antd';
import {
  updateProductDomainSession,
  getProductDomainSession,
  type ProductDomainSession,
} from '../../services/productDomainSessionApi';

const { Text } = Typography;

interface ProductContextScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const ProductContextScreen: React.FC<ProductContextScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [session, setSession] = useState<ProductDomainSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const load = async () => {
      const s = await getProductDomainSession(sessionId);
      setSession(s);
      if (s) {
        form.setFieldsValue({
          product_name: s.product_name ?? '',
          product_state: s.product_state || 'active',
          product_type: s.product_type ?? 'one_time',
        });
      }
      setLoading(false);
    };
    load();
  }, [sessionId, form]);

  const handleSaveAndNext = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const updated = await updateProductDomainSession(sessionId, {
      product_name: values.product_name || null,
      product_state: values.product_state,
      product_type: values.product_type,
    });
    setSaving(false);
    if (updated) {
      setSession(updated);
      onNext();
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Card title="Product Context" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Product</Text>
            <div>
              <Text strong>{session?.product_name || '—'}</Text>
            </div>
          </div>
          <div>
            <Text type="secondary">State</Text>
            <div>
              <Text>{session?.product_state || '—'}</Text>
            </div>
          </div>
          <div>
            <Text type="secondary">Type</Text>
            <div>
              <Text>{session?.product_type || '—'}</Text>
            </div>
          </div>
        </Space>
      </Card>

      <Card title="Edit Product">
        <Form form={form} layout="vertical">
          <Form.Item
            name="product_name"
            label="Product Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. Premium Subscription" />
          </Form.Item>
          <Form.Item
            name="product_state"
            label="Product State"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'active', label: 'Active' },
                { value: 'development', label: 'In Development' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="product_type"
            label="Product Type"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'one_time', label: 'One-time' },
                { value: 'recurring', label: 'Recurring' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSaveAndNext} loading={saving}>
              Save & Next
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
